package fr.frux.shared.identity

/**
 * Politique d'identité — email et pseudo (#25, ADR 0012).
 *
 * Elle vit dans `backend-shared` parce que c'est le seul module compilé par les **deux** builds
 * (racine KMP et backend standalone) : l'API et les écrans appliquent littéralement le même code,
 * pas deux copies qui divergent au premier ajustement. Le critère « même règle côté API et UI »
 * n'est donc pas une convention à surveiller, c'est une dépendance.
 *
 * Plus de règles de mot de passe : l'authentification est passwordless par code OTP email
 * (ADR 0012), il n'existe plus de secret durable à valider.
 *
 * Les libellés portés par [IdentityProblem] sont en anglais : ce sont ceux de l'enveloppe
 * `ErrorResponse`, alignés sur le reste de l'API. L'UI, elle, traduit à partir du problème typé.
 */
object IdentityPolicy {
    /** Longueur du code OTP (ADR 0012). Partagée pour que l'UI dessine autant de cases. */
    const val OTP_CODE_LENGTH = 6

    /**
     * Cadence minimale entre deux envois de code pour une même adresse (anti-rafale, #274).
     * Partagée pour que le compte à rebours des clients (#261) reflète la fenêtre réelle du
     * serveur : pendant ce délai, un renvoi est accepté en apparence mais aucun email ne part.
     */
    const val OTP_RESEND_COOLDOWN_SECONDS = 60

    const val MIN_USERNAME_LENGTH = 3
    const val MAX_USERNAME_LENGTH = 30

    /** Longueur maximale d'une adresse selon la RFC 5321. */
    const val MAX_EMAIL_LENGTH = 254

    /**
     * Normalisation d'email : c'est **elle** qui définit l'unicité, pas la casse saisie. Sans ça,
     * `Fred@x.fr` et `fred@x.fr` sont deux comptes pour une seule boîte aux lettres — et un
     * attaquant choisit la casse pour contourner un blocage nominatif.
     *
     * Volontairement limitée à `trim` + minuscules : pas de retrait des points ni du `+tag`, qui
     * dépendent du fournisseur et casseraient des adresses légitimes ailleurs.
     */
    fun normalizeEmail(raw: String): String = raw.trim().lowercase()

    /** La casse du pseudo est conservée pour l'affichage ; l'unicité, elle, est insensible (V7). */
    fun normalizeUsername(raw: String): String = raw.trim()

    /** `null` si l'adresse normalisée est acceptable. */
    fun validateEmail(raw: String): IdentityProblem? {
        val email = normalizeEmail(raw)
        return when {
            email.isEmpty() -> {
                IdentityProblem.EMAIL_REQUIRED
            }

            email.length > MAX_EMAIL_LENGTH -> {
                IdentityProblem.EMAIL_TOO_LONG
            }

            !EMAIL_FORMAT.matches(email) -> {
                IdentityProblem.EMAIL_INVALID
            }

            // Le motif ci-dessus autorise les points dans la partie locale sans contraindre leur
            // position ; ces trois cas restent invalides côté RFC.
            email.substringBefore('@').let { it.startsWith('.') || it.endsWith('.') } -> {
                IdentityProblem.EMAIL_INVALID
            }

            email.contains("..") -> {
                IdentityProblem.EMAIL_INVALID
            }

            // `.invalid` (RFC 2606) est le TLD que `UserDao.deletedEmail` utilise comme placeholder
            // d'anonymisation (#172) : si l'inscription l'acceptait, un attaquant pourrait préempter
            // `deleted+<id>@frux.invalid` d'un compte visé et faire échouer son effacement sur une
            // violation d'unicité. `.test` reste accepté : c'est le TLD des tests et du smoke.
            email.endsWith(".invalid") -> {
                IdentityProblem.EMAIL_INVALID
            }

            else -> {
                null
            }
        }
    }

    /** `null` si le pseudo normalisé est acceptable. */
    fun validateUsername(raw: String): IdentityProblem? {
        val username = normalizeUsername(raw)
        return when {
            username.isEmpty() -> IdentityProblem.USERNAME_REQUIRED
            username.length < MIN_USERNAME_LENGTH -> IdentityProblem.USERNAME_TOO_SHORT
            username.length > MAX_USERNAME_LENGTH -> IdentityProblem.USERNAME_TOO_LONG
            !USERNAME_FORMAT.matches(username) -> IdentityProblem.USERNAME_INVALID
            isReservedUsername(username) -> IdentityProblem.USERNAME_RESERVED
            else -> null
        }
    }

    /**
     * Pseudos que personne ne peut s'attribuer (#485), casse ignorée : noms qui se feraient passer
     * pour l'équipe ou pour un compte système (`openstreetmap` est un compte d'import, son pseudo
     * porte l'attribution ODbL), et le motif `cueilleur-<chiffres>` que la modération pose à la
     * réinitialisation d'un pseudo. Sans lui, un pseudo réinitialisé serait indiscernable d'un
     * pseudo choisi, et l'inscription pourrait préempter le pseudo d'un compte à réinitialiser.
     * Ne s'applique qu'à la création : un compte existant garde son pseudo, réinitialisé compris.
     */
    private fun isReservedUsername(username: String): Boolean {
        val lower = username.lowercase()
        return lower in RESERVED_USERNAMES || RESET_USERNAME_PATTERN.matches(lower)
    }

    private val RESERVED_USERNAMES =
        setOf(
            "admin",
            "administrateur",
            "frux",
            "moderation",
            "moderateur",
            "support",
            "contact",
            "openstreetmap",
        )

    private val RESET_USERNAME_PATTERN = Regex("^cueilleur-[0-9]+\$")

    // Motif HTML5 pour la partie locale, resserré côté domaine : au moins un point et des labels
    // qui ne commencent ni ne finissent par un tiret. `a@b` — accepté par la RFC, jamais routable
    // en pratique — est donc refusé, avant que l'utilisateur n'attende un mail qui n'arrivera pas.
    private val EMAIL_FORMAT =
        Regex(
            "^[a-z0-9.!#\$%&'*+/=?^_`{|}~-]+" +
                "@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?" +
                "(?:\\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+\$",
        )

    // Commence et finit par un alphanumérique : interdit ` .fred` et `fred-`, qui ne se distinguent
    // pas visuellement de `fred` une fois affichés dans une liste.
    private val USERNAME_FORMAT = Regex("^[a-zA-Z0-9][a-zA-Z0-9._-]*[a-zA-Z0-9]\$")
}

/**
 * Cause typée d'un refus d'identité. Un seul type pour les deux champs : la route en tire un
 * `when` unique, et l'UI n'a qu'une table de traduction à tenir.
 *
 * [code] est ce que reçoit le client dans `ErrorResponse.code`. La valeur historique
 * `INVALID_EMAIL` est conservée : des clients déjà livrés la lisent.
 */
enum class IdentityProblem(
    val code: String,
    val message: String,
) {
    EMAIL_REQUIRED("INVALID_EMAIL", "Email is required"),
    EMAIL_INVALID("INVALID_EMAIL", "Invalid email format"),
    EMAIL_TOO_LONG("INVALID_EMAIL", "Email must be at most ${IdentityPolicy.MAX_EMAIL_LENGTH} characters"),

    USERNAME_REQUIRED("INVALID_USERNAME", "Username is required"),
    USERNAME_TOO_SHORT(
        "INVALID_USERNAME",
        "Username must be at least ${IdentityPolicy.MIN_USERNAME_LENGTH} characters",
    ),
    USERNAME_TOO_LONG(
        "INVALID_USERNAME",
        "Username must be at most ${IdentityPolicy.MAX_USERNAME_LENGTH} characters",
    ),
    USERNAME_INVALID(
        "INVALID_USERNAME",
        "Username may only contain letters, digits, dot, dash and underscore, and must start and end with a letter or digit",
    ),
    USERNAME_RESERVED("INVALID_USERNAME", "Username is reserved"),

    ;

    /** Le champ que l'utilisateur doit corriger — l'UI s'en sert pour cibler l'erreur. */
    val field: IdentityField
        get() =
            when (this) {
                EMAIL_REQUIRED, EMAIL_INVALID, EMAIL_TOO_LONG -> {
                    IdentityField.EMAIL
                }

                USERNAME_REQUIRED, USERNAME_TOO_SHORT, USERNAME_TOO_LONG, USERNAME_INVALID, USERNAME_RESERVED -> {
                    IdentityField.USERNAME
                }
            }
}

enum class IdentityField { EMAIL, USERNAME }
